import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, History, Target, Linkedin } from 'lucide-react';
import { aboutContent, keyManagement, stats, images } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FAFFEE] to-[#EDEDFE] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-40 w-96 h-96 bg-[#CACAFC]/30 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#0C6951] font-semibold uppercase tracking-wider">
              <Link to="/" className="hover:text-[#004534]">Home</Link>
              <ChevronRight className="w-4 h-4" />
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#004534] mt-4 mb-6 leading-tight">
              About MLDC Agro
            </h1>
            <p className="text-xl text-[#0C6951] leading-relaxed">
              A farmer-centric organization building a financially stronger future for Indian agriculture since the 1960s.
            </p>
          </div>
        </div>
      </section>

      {/* Our History */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-3 text-[#0C6951]">
                <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center">
                  <History className="w-6 h-6 text-[#004534]" />
                </div>
                <span className="font-semibold uppercase tracking-wider">Our History</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] leading-tight">
                Six Decades of Agricultural Excellence
              </h2>
              <p className="text-[#807979] leading-relaxed text-lg">
                {aboutContent.history.description}
              </p>
              <div className="grid grid-cols-2 gap-6 pt-4">
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="bg-[#FAFFEE] rounded-2xl p-6">
                    <div className="text-3xl font-bold text-[#004534]">{stat.value}</div>
                    <div className="text-sm text-[#807979]">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src={images.farming}
                alt="Indian farmer in rice paddy"
                className="rounded-3xl w-full h-[400px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#004534] rounded-2xl p-6 text-white">
                <div className="text-4xl font-bold">1960s</div>
                <div className="text-white/80">Founded in Anupshahr</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-[#FAFFEE]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img
                src={images.wheat}
                alt="Golden grain field"
                className="rounded-3xl w-full h-[400px] object-cover shadow-xl"
              />
            </div>
            
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-3 text-[#0C6951]">
                <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#004534]" />
                </div>
                <span className="font-semibold uppercase tracking-wider">Our Commitment</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] leading-tight">
                Transparency & Trust at Our Core
              </h2>
              <p className="text-[#807979] leading-relaxed text-lg">
                {aboutContent.commitment.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="inline-flex items-center gap-3 text-[#0C6951] justify-center mb-4">
              <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-[#004534]" />
              </div>
              <span className="font-semibold uppercase tracking-wider">Our Team</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6">
              Key Management
            </h2>
            <p className="text-[#807979] leading-relaxed">
              {aboutContent.team.description}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {keyManagement.map((member) => (
              <div
                key={member.id}
                className="bg-[#FAFAFF] rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#004534]">{member.name}</h3>
                    <p className="text-[#0C6951] font-medium mb-3">{member.title}</p>
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#004534] hover:text-[#0C6951] transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span className="text-sm font-medium">Connect</span>
                    </a>
                  </div>
                </div>
                <p className="text-[#807979] mt-4 leading-relaxed text-sm">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-[#004534]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-white mb-4">{aboutContent.vision.title}</h3>
              <p className="text-white/80 leading-relaxed">{aboutContent.vision.description}</p>
            </div>
            
            <div className="bg-[#D3FF62] rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-[#004534] mb-4">{aboutContent.mission.title}</h3>
              <p className="text-[#004534]/80 leading-relaxed">{aboutContent.mission.description}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
